import { atom, useRecoilValue, selectorFamily, selector } from "recoil"

import type { User, Permission } from "~/graphql/client"
import { MIN_PROPOSAL_MARGIN } from "~/utils/finance/constants"

import { DEFAULT_USER } from "./constants"
import { designRequestState } from "../design-request/use-design-request"
import { proposalLocalState } from "../proposal/proposal-state"

export const currentUserState = atom<User>({
  key: `currentUser`,
  default: DEFAULT_USER,
})

export const currentPermissionsState = selector<User[`permissions`]>({
  key: `currentPermissions`,
  get: ({ get }) => {
    const user = get(currentUserState)

    return user.permissions
  },
})

export const currentUserMinimumMarginState = selector<number>({
  key: `currentUserMinimumMarginState`,
  get: ({ get }) => {
    const permissions = get(currentPermissionsState)
    const proposal = get(proposalLocalState)

    const enforceMinimumMargin = (margin: number) => {
      return margin >= MIN_PROPOSAL_MARGIN ? margin : MIN_PROPOSAL_MARGIN
    }

    if (!proposal.enterpriseSettings) return MIN_PROPOSAL_MARGIN
    const settings = proposal.enterpriseSettings
    if (permissions.includes(`has_no_enterprise_margin_limit`)) {
      return MIN_PROPOSAL_MARGIN
    }
    if (permissions.includes(`may_use_lower_enterprise_margin_limit`)) {
      if (settings.managerMarginAdjustment) {
        const lowestMargin = Math.min(
          settings.proposalMargin,
          settings.managerMinimumMargin,
        )

        return enforceMinimumMargin(lowestMargin)
      }
    }
    if (!settings.marginFixed) {
      return enforceMinimumMargin(settings.proposalMargin)
    }

    return enforceMinimumMargin(proposal.margin)
  },
})

export const currentUserIsDesignRequestUserState = selector<boolean>({
  key: `currentUserIsDesignRequestUser`,
  get: ({ get }) => {
    //This check disables blocking the user in the proposal design tool\\
    const { pathname } = window.location
    const isServiceQueueRoute = pathname.includes(`service-queue`)
    if (!isServiceQueueRoute) return true
    /////////////////////\\\\\\\\\\\\\\\\\\\\\\\\\

    const { id } = get(currentUserState)
    const { designUser } = get(designRequestState) ?? {}

    return id === designUser?.userId
  },
})

// stop using this, replace with useDoesUserHaveRequiredPermissions instead
export const userHasRequiredPermissionsState = selectorFamily<
  boolean,
  readonly Permission[]
>({
  key: `userHasRequiredPermissions`,
  get:
    (allowedPermissions) =>
    ({ get }) => {
      const permissions = get(currentPermissionsState)

      return allowedPermissions.some((permission) =>
        permissions.includes(permission),
      )
    },
})

export const useDoesUserHaveRequiredPermissions = (
  required: readonly Permission[],
): boolean => {
  const { permissions: current } = useRecoilValue(currentUserState)

  return required.some((permission) => current.includes(permission))
}
